/**
 * Admin API-client met auto-refresh, 401-handling en token-storage.
 *
 * Stack-keuze: localStorage voor tokens. Conform spec §"AUTH".
 * TODO v1.5: migrate naar httpOnly secure-cookie via een lichte BFF-proxy
 *            zodat XSS-risico verdwijnt en CSRF-token via dubbele-cookie wordt afgehandeld.
 */
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REFRESH_LEAD_MS = 4 * 60 * 1000; // refresh 4 min vóór expiry
const STORAGE_KEY = "motera_admin_session";

let _refreshTimer = null;
let _onUnauthorized = null;

// -------- Storage --------
export function loadSession() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.access_token || !s.refresh_token || !s.expires_at) return null;
    return s;
  } catch {
    return null;
  }
}

export function saveSession(session) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSession() {
  window.localStorage.removeItem(STORAGE_KEY);
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
}

export function isExpired(session, nowMs = Date.now()) {
  if (!session || !session.expires_at) return true;
  return nowMs >= session.expires_at;
}

// -------- Axios instance --------
const api = axios.create({
  baseURL: BACKEND,
  timeout: 15000,
});

api.interceptors.request.use((config) => {
  const s = loadSession();
  if (s?.access_token) {
    config.headers.Authorization = `Bearer ${s.access_token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (_onUnauthorized) _onUnauthorized();
    }
    return Promise.reject(error);
  },
);

export function setUnauthorizedHandler(fn) {
  _onUnauthorized = fn;
}

// -------- Auth-endpoints --------
export async function adminLogin(email, password) {
  const res = await axios.post(`${BACKEND}/api/auth/admin/login`, { email, password }, { timeout: 15000 });
  return _materialise(res.data);
}

export async function adminRefresh(refreshToken) {
  const res = await axios.post(`${BACKEND}/api/auth/admin/refresh`, { refresh_token: refreshToken }, { timeout: 15000 });
  return _materialise(res.data);
}

export async function adminMe() {
  const res = await api.get("/api/auth/admin/me");
  return res.data;
}

export async function adminLogout() {
  try {
    await api.post("/api/auth/admin/logout");
  } catch {
    // best-effort
  }
  clearSession();
}

// -------- MFA --------
export async function mfaStatus() {
  const res = await api.get("/api/auth/admin/mfa/status");
  return res.data;
}

export async function mfaChallenge(factorId) {
  const res = await api.post("/api/auth/admin/mfa/challenge", { factor_id: factorId });
  return res.data;
}

export async function mfaVerifyChallenge(factorId, challengeId, code) {
  const res = await api.post("/api/auth/admin/mfa/verify-challenge", {
    factor_id: factorId, challenge_id: challengeId, code,
  });
  return res.data;
}

export async function mfaEnroll() {
  const res = await api.post("/api/auth/admin/mfa/enroll", {});
  return res.data;
}

export async function mfaVerifyEnrollment(factorId, code) {
  const res = await api.post("/api/auth/admin/mfa/verify-enrollment", { factor_id: factorId, code });
  return res.data;
}

// -------- Helpers --------
function _materialise(tokens) {
  // Voegt expires_at (ms-epoch) toe op basis van expires_in (s) — handig voor expiry-check.
  const expires_at = Date.now() + (tokens.expires_in - 30) * 1000;
  return { ...tokens, expires_at };
}

export function scheduleRefresh(session, onRefreshed, onFailed) {
  if (_refreshTimer) clearTimeout(_refreshTimer);
  if (!session) return;
  const delay = Math.max(0, session.expires_at - Date.now() - REFRESH_LEAD_MS);
  _refreshTimer = setTimeout(async () => {
    try {
      const refreshed = await adminRefresh(session.refresh_token);
      const next = { ...session, ...refreshed };
      saveSession(next);
      onRefreshed?.(next);
      scheduleRefresh(next, onRefreshed, onFailed);
    } catch (e) {
      clearSession();
      onFailed?.(e);
    }
  }, delay);
}

// -------- Generic GET helper voor lists --------
export async function adminGet(path, params) {
  const res = await api.get(path, { params });
  return res.data;
}

/**
 * formatApiError(err) — normaliseert backend-fouten naar een leesbare string.
 * Dekt:
 *  - axios-error met FastAPI string-detail
 *  - axios-error met FastAPI array-detail (422 validation)
 *  - axios-error met object-detail (custom error_code+message)
 *  - error_code op root van data
 *  - fallback naar err.message of "Onbekende fout"
 *
 * Voorkomt "[object Object]" in toasts/banners.
 */
export function formatApiError(err) {
  if (!err) return "Onbekende fout";
  if (typeof err === "string") return err;
  const data = err?.response?.data;

  if (data) {
    const detail = data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      // FastAPI 422-validation: [{loc:[...], msg:"...", type:"..."}]
      return detail
        .map((d) => {
          if (!d) return "";
          if (typeof d === "string") return d;
          const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
          const msg = d.msg || d.message || "";
          return loc ? `${loc}: ${msg}` : msg;
        })
        .filter(Boolean)
        .join("; ") || "Validatiefout";
    }
    if (detail && typeof detail === "object") {
      const code = detail.error_code || data.error_code;
      const msg = detail.message || detail.msg;
      if (code && msg) return `${code}: ${msg}`;
      if (code) return String(code);
      if (msg) return String(msg);
    }
    if (data.error_code) {
      return data.message ? `${data.error_code}: ${data.message}` : String(data.error_code);
    }
    if (typeof data.message === "string") return data.message;
  }
  return err?.message || "Onbekende fout";
}

export async function adminPost(path, body, config) {
  const res = await api.post(path, body, config);
  return res.data;
}

export async function adminPatch(path, body) {
  const res = await api.patch(path, body);
  return res.data;
}

export async function adminDelete(path) {
  const res = await api.delete(path);
  return res.data;
}

export function adminApiInstance() {
  return api;
}

export { BACKEND };
